import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Space, Tag, Typography, List } from 'antd';
import {
  ClusterOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { DISCOVERY_API_BASE } from './api';
import { useNavigate } from 'react-router-dom';
import { ClusterConfig } from '../types';

const { Text, Title } = Typography;

const PLATFORM_COLORS: Record<string, string> = {
  kubernetes: 'blue',
  openshift: 'red',
  deckhouse: 'purple',
};

const HEALTH_COLORS: Record<string, string> = {
  healthy: 'success',
  degraded: 'warning',
  unreachable: 'error',
  unknown: 'default',
};

const KubernetesDiscoveryDashboard: React.FC = () => {
  const [clusters, setClusters] = useState<ClusterConfig[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [topology, setTopology] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const loadClusters = useCallback(async () => {
    try {
      const response = await fetch(`${DISCOVERY_API_BASE}/clusters/configs`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: ClusterConfig[] = await response.json();
      setClusters(data || []);
      if (data && data.length > 0 && !selectedCluster) {
        setSelectedCluster(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load clusters:', error);
    }
  }, []);

  useEffect(() => {
    loadClusters();
  }, [loadClusters]);

  useEffect(() => {
    if (selectedCluster) {
      loadPlatformResources(selectedCluster);
    }
  }, [selectedCluster]);

  const loadPlatformResources = async (configId: string) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${DISCOVERY_API_BASE}/clusters/${configId}/platform-resources`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setTopology(data);
    } catch (error) {
      console.error('Failed to load platform resources:', error);
      setTopology(null);
    } finally {
      setLoading(false);
    }
  };

  const startDiscovery = async (configId?: string) => {
    setLoading(true);
    try {
      const url = configId
        ? `${DISCOVERY_API_BASE}/discovery/${configId}/start`
        : `${DISCOVERY_API_BASE}/discovery/start-all`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // Fire-and-forget — no job ID, no polling
      setTimeout(loadClusters, 3000);
    } catch (error) {
      console.error('Failed to start discovery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const selectedClusterData = clusters.find((c) => c.id === selectedCluster);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  <ClusterOutlined /> Kubernetes Discovery Dashboard
                </Title>
                {selectedClusterData && (
                  <Text type="secondary">
                    Current cluster: {selectedClusterData.display_name}
                  </Text>
                )}
              </Col>
              <Col>
                <Space>
                  <Button onClick={handleBack}>Назад</Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadClusters}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={() => startDiscovery()}
                    loading={loading}
                  >
                    Start All
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[16, 16]}>
        {/* Cluster List */}
        <Col xs={24} lg={12}>
          <Card title="Clusters" loading={loading && clusters.length === 0}>
            <List<ClusterConfig>
              dataSource={clusters}
              locale={{ emptyText: 'No clusters found. Start discovery to populate.' }}
              renderItem={(cluster) => (
                <List.Item
                  key={cluster.id}
                  style={{
                    cursor: 'pointer',
                    backgroundColor:
                      cluster.id === selectedCluster ? '#f0f5ff' : undefined,
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '8px',
                  }}
                  onClick={() => setSelectedCluster(cluster.id)}
                  actions={[
                    <Button
                      key="start"
                      size="small"
                      icon={<PlayCircleOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        startDiscovery(cluster.id);
                      }}
                      loading={loading && cluster.id === selectedCluster}
                    >
                      Start discovery
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{cluster.display_name}</Text>
                        {cluster.platform && (
                          <Tag color={PLATFORM_COLORS[cluster.platform] || 'default'}>
                            {cluster.platform}
                          </Tag>
                        )}
                        {cluster.health_status && (
                          <Tag
                            color={
                              HEALTH_COLORS[cluster.health_status.toLowerCase()] ||
                              'default'
                            }
                          >
                            {cluster.health_status}
                          </Tag>
                        )}
                        <Tag color={cluster.is_active ? 'green' : 'default'}>
                          {cluster.is_active ? 'Active' : 'Inactive'}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        {cluster.description && (
                          <Text type="secondary">{cluster.description}</Text>
                        )}
                        {cluster.last_health_check && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Last check:{' '}
                            {new Date(cluster.last_health_check).toLocaleString()}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Platform Resources */}
        <Col xs={24} lg={12}>
          <Card
            title={
              selectedClusterData
                ? `Platform Resources — ${selectedClusterData.display_name}`
                : 'Platform Resources'
            }
            loading={loading}
          >
            {topology ? (
              <pre
                style={{
                  overflow: 'auto',
                  maxHeight: '600px',
                  background: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  margin: 0,
                }}
              >
                {JSON.stringify(topology, null, 2)}
              </pre>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                {selectedCluster
                  ? 'No platform resources available for this cluster.'
                  : 'Select a cluster to view platform resources.'}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default KubernetesDiscoveryDashboard;
